import type { JSX, ReactNode } from "react";
import { ExternalLink, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TabWebTarget } from "@/lib/webHints/types";
import { webHintText } from "./FieldHint";

const ROW = "inline-flex items-center gap-1.5 text-sm";

/**
 * Odkaz „Zobrazit na webu“ editoru (řez 10, F9.6 bod 5): odkaz záložky v obalu
 * `[data-tab-web-link="<id záložky>"]` nad obsahem záložky, nebo odkaz hlavičky bez obalu.
 * Všechny odkazy na web v šesti editorech jdou přes tuto komponentu, takže stav publikace platí
 * všude. Obal a umístění dělá editor, komponenta jen prvek `[data-web-link]`.
 *
 * `pageUrl` = `WEB_URL` + `routes.*` stránky, skládá ho editor (u cíle `{ page: "offer" }`
 * záložka Nabídka); komponenta přidá jen `#<anchor>`.
 *
 * Rozhodovací pořadí (hodnota `data-web-link` nese stav):
 * 1. `{ notShown }` → nic;
 * 2. `{ backoffice }` → `backoffice`, věta, k čemu záložka slouží (i u publikovaného obsahu);
 * 3. nová položka, nebo chybí adresa (`pageUrl === null`, např. bez slugu) → `new`;
 * 4. nepublikovaný obsah → `unpublished`; náhled se nestaví (Ne-cíl), odkaz jen vysvětlí proč;
 * 5. jinak `active`: `<a target="_blank">` na `pageUrl`, s kotvou, je-li.
 *
 * Neaktivní prvek nemá `href` ani `tabIndex` a vysvětlení nese uvnitř sebe (E2E ho hledá
 * v `[data-web-link]`).
 */
export default function TabWebLink({
  target,
  pageUrl,
  published,
  isNew,
}: {
  target: TabWebTarget;
  pageUrl: string | null;
  published: boolean;
  isNew: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  if ("notShown" in target) return null;

  if ("backoffice" in target) {
    return (
      <Disabled state="backoffice">
        {t("webHints.common.tabBackoffice", { purpose: webHintText(t, target.backoffice) })}
      </Disabled>
    );
  }
  if (isNew || pageUrl === null) {
    return (
      <Disabled state="new">
        {`${t("webHints.common.viewOnWeb")} (${t("webHints.common.notSavedYet")})`}
      </Disabled>
    );
  }
  if (!published) {
    return (
      <Disabled state="unpublished">
        {`${t("webHints.common.viewOnWeb")} (${t("webHints.common.notPublished")})`}
      </Disabled>
    );
  }

  const href = "anchor" in target && target.anchor ? `${pageUrl}#${target.anchor}` : pageUrl;
  return (
    <a
      data-web-link="active"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${ROW} text-primary-600 hover:text-primary-700 font-medium`}
    >
      <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
      {t("webHints.common.viewOnWeb")}
    </a>
  );
}

function Disabled({
  state,
  children,
}: {
  state: "backoffice" | "new" | "unpublished";
  children: ReactNode;
}) {
  return (
    <span data-web-link={state} role="link" aria-disabled="true" className={`${ROW} text-gray-500`}>
      <EyeOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </span>
  );
}
